<script setup lang="ts">
/**
 * Репозитории — макет 152:10262: сводка карточками, таблица репозиториев с
 * состоянием вебхука, подсказка про сверку внизу.
 *
 * Экран отвечает на вопрос, на который зеркало веток и пайплайнов не отвечает:
 * «откуда события не приходят». Репозиторий без хука молчит ровно так же, как
 * репозиторий, где ничего не происходило.
 *
 * Кнопки «Сохранить» тут нет намеренно: подключение и отключение — запись в
 * GitLab, откатывать её черновиком нечем. Уходит сразу, как пресет колонок и
 * архивирование проекта.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { AlertTriangle, ExternalLink, Folder, Plug, RefreshCw, X } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import FilterChip from '@/components/FilterChip.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useAuthStore } from '@/features/auth/store'
import DevOpsMetric from '@/features/devops/components/DevOpsMetric.vue'
import { formatUptime } from '@/features/devops/format'
import {
  connectRepository,
  disconnectRepository,
  listRepositories,
  syncRepositories,
  WEBHOOK_STATES,
  type Repository,
  type RepositoryTotals,
  type WebhookState,
} from '@/features/devops/repositories'

const { t } = useI18n()
const toast = useToast()
const auth = useAuthStore()

const repositories = ref<Repository[]>([])
const totals = ref<RepositoryTotals>({
  total: 0,
  connected: 0,
  missing: 0,
  failed: 0,
  syncedAt: '',
})
const isLoading = ref(false)
const isSyncing = ref(false)
const busyId = ref(0)
const error = ref('')
const stateFilter = ref<string[]>([])

/** Права те же, что у остальных DevOps-кнопок: запись идёт в чужую систему. */
const allowed = computed(() => auth.can('tracker:devops'))

const stateOptions = computed(() =>
  WEBHOOK_STATES.map((value) => ({ value, label: t(`devops.repos.state.${value}`) })),
)

const visible = computed(() => {
  if (!stateFilter.value.length) return repositories.value

  return repositories.value.filter((repo) => stateFilter.value.includes(repo.state))
})

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    const data = await listRepositories()
    repositories.value = data.repositories
    totals.value = data.totals
  } catch (failure) {
    error.value = errorMessage(failure, t('devops.repos.error'))
  } finally {
    isLoading.value = false
  }
}

const sync = async () => {
  isSyncing.value = true

  try {
    const report = await syncRepositories()

    // Отчёт показываем числами, а не «готово»: сверка чаще всего не меняет
    // ничего, и человеку важно видеть именно это, а не гадать.
    toast.success(
      t('devops.repos.syncDone', {
        checked: report.checked,
        connected: report.connected + report.repaired,
        failed: report.failed,
      }),
    )

    await load()
  } catch (failure) {
    toast.error(errorMessage(failure, t('devops.repos.syncFailed')))
  } finally {
    isSyncing.value = false
  }
}

const connect = async (repo: Repository) => {
  busyId.value = repo.id

  try {
    await connectRepository(repo.id)
    toast.success(t('devops.repos.connected', { path: repo.path }))
    await load()
  } catch (failure) {
    toast.error(errorMessage(failure, t('devops.repos.connectFailed')))
  } finally {
    busyId.value = 0
  }
}

const disconnect = async (repo: Repository) => {
  busyId.value = repo.id

  try {
    await disconnectRepository(repo.id)
    toast.success(t('devops.repos.disconnected', { path: repo.path }))
    await load()
  } catch (failure) {
    toast.error(errorMessage(failure, t('devops.repos.disconnectFailed')))
  } finally {
    busyId.value = 0
  }
}

/** Единицы те же, что у аптайма стендов: колонки соседних экранов читаются одинаково. */
const uptimeLabels = () => ({
  days: t('devops.envs.uptime.days'),
  hours: t('devops.envs.uptime.hours'),
  minutes: t('devops.envs.uptime.minutes'),
})

const ago = (iso: string) => t('devops.repos.ago', { time: formatUptime(iso, uptimeLabels()) })

/** Событий не было вовсе — это не «давно», а «ни разу». */
const eventLabel = (repo: Repository) =>
  repo.lastEventAt ? ago(repo.lastEventAt) : t('devops.repos.noEvents')

const stateTone = (state: WebhookState) => `repos__badge--${state}`

onMounted(load)
</script>

<template>
  <TrackerToolbar
    :breadcrumbs="[{ label: t('nav.devops'), to: '/devops' }, { label: t('devops.repos.title') }]"
  >
    <FilterChip
      v-model="stateFilter"
      :label="t('devops.repos.filters.state')"
      :options="stateOptions"
      :empty-text="t('devops.repos.empty')"
    />

    <AppButton v-if="allowed" variant="primary" size="small" :loading="isSyncing" @click="sync">
      {{ t('devops.repos.sync') }}
    </AppButton>
  </TrackerToolbar>

  <div class="repos">
    <div class="repos__metrics">
      <DevOpsMetric
        :label="t('devops.repos.metrics.connected')"
        :value="totals.connected"
        :hint="t('devops.repos.metrics.connectedHint', { total: totals.total })"
        tone="success"
      />
      <DevOpsMetric
        :label="t('devops.repos.metrics.missing')"
        :value="totals.missing"
        :hint="t('devops.repos.metrics.missingHint')"
        tone="warning"
      />
      <DevOpsMetric
        :label="t('devops.repos.metrics.failed')"
        :value="totals.failed"
        :hint="t('devops.repos.metrics.failedHint')"
        tone="danger"
      />
      <DevOpsMetric
        :label="t('devops.repos.metrics.synced')"
        :value="totals.syncedAt ? ago(totals.syncedAt) : '—'"
        :hint="t('devops.repos.metrics.syncedHint')"
      />
    </div>

    <div v-if="isLoading && !repositories.length" class="repos__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('devops.repos.error')"
      :description="error"
      tint="var(--color-danger-tint)"
    />

    <TrackerEmptyState
      v-else-if="!visible.length"
      :icon="Folder"
      :title="t('devops.repos.empty')"
      :description="t('devops.repos.emptyHint')"
    />

    <div v-else class="repos__table">
      <div class="repos__head">
        <span class="repos__col repos__col--path">{{ t('devops.repos.columns.path') }}</span>
        <span class="repos__col repos__col--events">{{ t('devops.repos.columns.events') }}</span>
        <span class="repos__col repos__col--event">{{ t('devops.repos.columns.lastEvent') }}</span>
        <span class="repos__col repos__col--state" />
        <span class="repos__col repos__col--actions">{{ t('devops.repos.columns.actions') }}</span>
      </div>

      <div v-for="repo in visible" :key="repo.id" class="repos__row">
        <span class="repos__col repos__col--path">
          <Folder :size="16" class="repos__icon" />
          <span class="repos__path">{{ repo.path }}</span>
        </span>

        <span class="repos__col repos__col--events">
          {{ repo.state === 'connected' ? t('devops.repos.events') : '—' }}
        </span>

        <span class="repos__col repos__col--event">{{ eventLabel(repo) }}</span>

        <span class="repos__col repos__col--state">
          <span class="repos__badge" :class="stateTone(repo.state)" :title="repo.error">
            {{ t(`devops.repos.state.${repo.state}`) }}
          </span>
        </span>

        <span class="repos__col repos__col--actions">
          <a
            :href="repo.webUrl"
            target="_blank"
            rel="noopener"
            class="repos__action"
            :aria-label="t('devops.repos.open')"
            :title="t('devops.repos.open')"
          >
            <ExternalLink :size="15" />
          </a>

          <button
            v-if="allowed && repo.state !== 'connected'"
            type="button"
            class="repos__action"
            :disabled="busyId === repo.id"
            :aria-label="t('devops.repos.connect')"
            :title="t('devops.repos.connect')"
            @click="connect(repo)"
          >
            <Plug :size="15" />
          </button>

          <button
            v-if="allowed && repo.state === 'connected'"
            type="button"
            class="repos__action"
            :disabled="busyId === repo.id"
            :aria-label="t('devops.repos.recheck')"
            :title="t('devops.repos.recheck')"
            @click="connect(repo)"
          >
            <RefreshCw :size="15" />
          </button>

          <button
            v-if="allowed && repo.state !== 'muted' && repo.state !== 'missing'"
            type="button"
            class="repos__action"
            :disabled="busyId === repo.id"
            :aria-label="t('devops.repos.disconnect')"
            :title="t('devops.repos.disconnect')"
            @click="disconnect(repo)"
          >
            <X :size="15" />
          </button>
        </span>
      </div>
    </div>

    <p class="repos__note">
      <AlertTriangle :size="16" />
      <span>{{ t('devops.repos.note') }}</span>
    </p>
  </div>
</template>

<style scoped>
/* Экран ровно по высоте окна за вычетом тулбара: репозиториев под сотню, и
   вместе со списком уезжала сводка, ради которой сюда и приходят (DEV-1587).
   Прокручивается только таблица. */
.repos {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  height: calc(100vh - 48px);
  padding: var(--spacing-lg) var(--spacing-xl);
}

.repos__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-sm);
}

.repos__skeleton {
  height: 240px;
  border-radius: var(--radius-lg);
  background: var(--color-surface-muted);
}

.repos__table {
  flex: 1;
  min-height: 0;
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  overflow: auto;
}

.repos__head,
.repos__row {
  display: grid;
  grid-template-columns: minmax(240px, 2fr) 180px 160px 130px 96px;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 0 var(--spacing-md);
}

/* Шапка таблицы остаётся на виду внутри её собственной прокрутки: без неё
   через двадцать строк непонятно, какая колонка что значит. */
.repos__head {
  position: sticky;
  top: 0;
  z-index: 1;
  height: 34px;
  background: var(--color-surface-muted);
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.repos__row {
  min-height: 52px;
  border-top: 1px solid var(--color-border);
  font-size: 13px;
  color: var(--color-text-secondary);
}

.repos__col--path {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
}

.repos__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.repos__path {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.repos__col--event {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-muted);
}

.repos__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 var(--spacing-xs);
  border-radius: var(--radius-sm);
  font-size: 11px;
  white-space: nowrap;
}

.repos__badge--connected {
  background: var(--color-success-tint);
  color: var(--color-success-strong);
}

.repos__badge--missing {
  background: var(--color-warning-tint);
  color: var(--color-warning-strong);
}

.repos__badge--failed {
  background: var(--color-danger-tint);
  color: var(--color-danger-strong);
}

.repos__badge--muted {
  background: var(--color-surface-muted);
  color: var(--color-text-muted);
}

.repos__col--actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-xs);
}

.repos__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
}

.repos__action:hover:not(:disabled) {
  background: var(--color-surface-muted);
  color: var(--color-text-primary);
}

.repos__action:disabled {
  cursor: progress;
  opacity: 0.5;
}

.repos__note {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-lg);
  background: var(--color-surface-muted);
  font-size: 13px;
  color: var(--color-text-secondary);
}

.repos__note svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--color-text-muted);
}
</style>
